import { useEffect, useState } from 'react';
import { api, errorText } from '../lib/api';
import { classNames, go, hashParam, useServerEvents } from '../lib/util';
import { DOC_KIND_LABEL } from './Documents';

export function DocumentDetail({ campaignId, documentId }: { campaignId: string; documentId: string }) {
  const [d, setD] = useState<any>(null);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [err, setErr] = useState('');
  const [page, setPage] = useState<number>(Number(hashParam('page') ?? 0) || 0);
  const load = async () => {
    try {
      setD(await api(`/api/documents/${documentId}`));
    } catch (e) {
      setErr(errorText(e));
    }
  };
  useEffect(() => {
    void load();
    api<any[]>('/api/campaigns').then(setCampaigns).catch(() => undefined);
  }, [documentId]);
  useServerEvents(null, (w) => w === 'documents' && void load());
  useEffect(() => {
    if (d?.document.status !== 'extracting') return;
    const t = window.setInterval(() => void load(), 2000);
    return () => clearInterval(t);
  }, [d?.document.status]);
  if (!d) return <div className="page">{err ? <p className="error">{err}</p> : <p className="muted">Loading…</p>}</div>;
  const doc = d.document;
  const act = async (fn: () => Promise<unknown>) => {
    setErr('');
    try {
      await fn();
      await load();
    } catch (x) {
      setErr(errorText(x));
    }
  };
  const p = d.pages.find((x: any) => x.page_index === page) ?? d.pages[0];
  const assignedIds = new Set(d.campaigns.map((c: any) => c.id));
  return (
    <div className="page">
      <p>
        <a href={`#/c/${campaignId}/documents`}>← Documents</a>
      </p>
      <div className="page-head">
        <div>
          <h1>{doc.title}</h1>
          <p className="muted">
            {DOC_KIND_LABEL[doc.kind]} · {doc.original_name} · {(doc.size_bytes / 1024).toFixed(0)} KB · {doc.status.replace('_', ' ')}
            {doc.progress_text ? ` · ${doc.progress_text}` : ''}
          </p>
        </div>
        <div className="row wrap">
          <a className="btn" href={`/api/documents/${doc.id}/original`}>
            Download original
          </a>
          <button className="btn" onClick={() => void act(() => api(`/api/documents/${doc.id}/retry`, { body: { campaignId } }))}>
            Retry extraction
          </button>
          <button
            className="btn danger"
            onClick={() => {
              if (confirm('Delete this document from every campaign? The original file is removed.')) void act(async () => { await api(`/api/documents/${doc.id}`, { method: 'DELETE' }); go(`/c/${campaignId}/documents`); });
            }}
          >
            Delete
          </button>
        </div>
      </div>
      {doc.error && <p className="error">{doc.error}</p>}
      {err && <p className="error">{err}</p>}

      <details className="card">
        <summary>Document settings & campaign assignment</summary>
        <DocSettings doc={doc} onSave={(body) => act(() => api(`/api/documents/${doc.id}`, { method: 'PATCH', body }))} />
        <h3>Used in campaigns</h3>
        <ul className="plain">
          {campaigns.map((c) => (
            <li key={c.id}>
              <label className="check">
                <input type="checkbox" checked={assignedIds.has(c.id)} onChange={(e) => void act(() => api(`/api/documents/${doc.id}/assign`, { body: { campaignId: c.id, assign: e.target.checked } }))} /> {c.name}
              </label>
            </li>
          ))}
        </ul>
      </details>

      <div className="doc-review">
        <nav className="page-list" aria-label="Pages">
          {d.pages.map((x: any) => (
            <button key={x.page_index} className={classNames('page-btn', x.page_index === p?.page_index && 'active', x.needs_review && 'review', x.status === 'failed' && 'failed')} onClick={() => setPage(x.page_index)}>
              {d.pages.length > 1 ? `p. ${x.page_index + 1}` : 'Text'}
              {x.page_label && x.page_label !== String(x.page_index + 1) ? ` (${x.page_label})` : ''}
              {x.needs_review ? ' ⚠' : ''}
            </button>
          ))}
        </nav>
        {p && <PageEditor key={`${p.page_index}:${p.corrected_at ?? ''}:${p.status}`} doc={doc} p={p} campaignId={campaignId} act={act} />}
      </div>
    </div>
  );
}

function PageEditor({ doc, p, campaignId, act }: { doc: any; p: any; campaignId: string; act: (fn: () => Promise<unknown>) => Promise<void> }) {
  const [text, setText] = useState(p.text);
  const dirty = text !== p.text;
  return (
    <section className="page-editor">
      <p className="muted small">
        PDF position {p.page_index + 1}
        {p.page_label ? ` · printed page label “${p.page_label}”` : ''} · {p.method === 'text' ? 'embedded text' : p.method === 'ocr' ? 'OCR (Tesseract)' : p.method === 'vision' ? 'AI transcription' : p.method === 'manual' ? 'corrected by you' : p.status}
        {p.confidence != null ? ` · confidence ${Math.round(p.confidence)}%` : ''}
      </p>
      {p.review_reason && <p className="warn">{p.review_reason}</p>}
      {p.error && <p className="error">{p.error}</p>}
      {doc.mime.startsWith('image/') && <img className="doc-image" src={`/api/documents/${doc.id}/original?inline=1`} alt={`Original of ${doc.title}`} />}
      <label className="sr-only" htmlFor="page-text">
        Extracted text
      </label>
      <textarea id="page-text" className="page-text" value={text} onChange={(e) => setText(e.target.value)} rows={18} />
      <div className="row wrap">
        <button className="btn primary" disabled={!dirty} onClick={() => void act(() => api(`/api/documents/${doc.id}/pages/${p.page_index}`, { method: 'PUT', body: { text } }))}>
          Save correction
        </button>
        {p.needs_review ? (
          <button className="btn" onClick={() => void act(() => api(`/api/documents/${doc.id}/pages/${p.page_index}/reviewed`, { body: {} }))}>
            Mark reviewed
          </button>
        ) : null}
        <button className="btn" onClick={() => void act(() => api(`/api/documents/${doc.id}/retry`, { body: { campaignId, ocrPages: [p.page_index], engine: 'tesseract' } }))}>
          Re-run OCR (Tesseract)
        </button>
        <button className="btn" onClick={() => void act(() => api(`/api/documents/${doc.id}/retry`, { body: { campaignId, ocrPages: [p.page_index], engine: 'vision' } }))}>
          Re-run with AI vision
        </button>
      </div>
    </section>
  );
}

function DocSettings({ doc, onSave }: { doc: any; onSave: (b: any) => void }) {
  const [f, setF] = useState({ title: doc.title, kind: doc.kind, gameSystem: doc.game_system, edition: doc.edition, visibility: doc.visibility });
  return (
    <form
      className="form-grid"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(f);
      }}
    >
      <label>
        Title
        <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
      </label>
      <label>
        Kind
        <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
          {Object.entries(DOC_KIND_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </label>
      <label>
        Game system
        <input value={f.gameSystem} onChange={(e) => setF({ ...f, gameSystem: e.target.value })} />
      </label>
      <label>
        Edition
        <input value={f.edition} onChange={(e) => setF({ ...f, edition: e.target.value })} />
      </label>
      <label>
        Visibility
        <select value={f.visibility} onChange={(e) => setF({ ...f, visibility: e.target.value })}>
          <option value="public">Public</option>
          <option value="gm_only">GM only</option>
        </select>
      </label>
      <div className="span2">
        <button className="btn primary">Save</button>
      </div>
    </form>
  );
}
